import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, HelpCircle, Save, Eye, MessageCircle, Play, Sparkles } from "lucide-react";
import ScenarioAnimation from "../components/driving/ScenarioAnimation";

const API = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { id: "preview",    label: "Preview",    icon: Eye },
  { id: "understand", label: "Understand", icon: Sparkles },
  { id: "ask",        label: "Ask",        icon: MessageCircle },
  { id: "execute",    label: "Execute",    icon: Play },
  { id: "reflect",    label: "Reflect",    icon: CheckCircle2 },
];

/**
 * LearnerScenario — Preview → Understand → Ask → Execute → Reflect loop.
 */
export default function LearnerScenario() {
  const { scenarioId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageIdx, setStageIdx] = useState(0);
  const [reflection, setReflection] = useState("");
  const [savingReadiness, setSavingReadiness] = useState(false);
  const [savingReflection, setSavingReflection] = useState(false);

  const load = async () => {
    try {
      const { data: payload } = await axios.get(`${API}/api/learn-mode/scenarios/${scenarioId}`, { withCredentials: true });
      setData(payload.scenario);
      setProgress(payload.progress);
      setReflection(payload.progress?.reflection || "");
    } catch {
      toast.error("Couldn't load scenario.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [scenarioId]);

  const setReadiness = async (state) => {
    setSavingReadiness(true);
    try {
      await axios.post(`${API}/api/learn-mode/scenarios/${scenarioId}/readiness`, { state }, { withCredentials: true });
      setProgress((p) => ({ ...(p || {}), readiness: state }));
      toast.success(state === "understand" ? "Locked in. Carry on." : "Got it — your instructor will help here.");
      // auto-advance to next stage on understand
      if (state === "understand" && stageIdx < STAGES.length - 1) setStageIdx(stageIdx + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSavingReadiness(false);
    }
  };

  const saveReflection = async () => {
    if (reflection.trim().length < 2) return toast.error("Add a few words first.");
    setSavingReflection(true);
    try {
      await axios.post(`${API}/api/learn-mode/scenarios/${scenarioId}/reflection`, { notes: reflection.trim() }, { withCredentials: true });
      toast.success("Saved — nice reflection.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSavingReflection(false);
    }
  };

  if (loading || !data) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-950"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  const stage = STAGES[stageIdx];

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-950 text-white pb-28">
      <header className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="ls-back" onClick={() => nav("/learn")} className="p-2 rounded-xl hover:bg-slate-800">
            <ArrowLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-indigo-400">Scenario</p>
            <p className="text-sm font-extrabold truncate">{data.title}</p>
          </div>
          {progress?.readiness === "understand" && (
            <span className="text-[10px] font-extrabold px-2 py-1 rounded-full bg-emerald-600 text-white" data-testid="ls-readiness-chip">Locked in</span>
          )}
          {progress?.readiness === "need_help" && (
            <span className="text-[10px] font-extrabold px-2 py-1 rounded-full bg-amber-600 text-white">Needs help</span>
          )}
        </div>
        {/* Stage tabs */}
        <div className="max-w-lg mx-auto px-4 pb-2 flex gap-1">
          {STAGES.map((s, i) => (
            <button
              key={s.id}
              data-testid={`ls-stage-${s.id}`}
              onClick={() => setStageIdx(i)}
              className={`flex-1 px-2 py-1.5 text-[10px] font-extrabold uppercase tracking-wider rounded-full ${
                stageIdx === i ? "bg-indigo-600 text-white" : "text-slate-400 hover:bg-slate-800"
              }`}
            >{s.label}</button>
          ))}
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
        {/* Always-visible animation */}
        <ScenarioAnimation animationKey={data.animation_key} />

        {/* Stage content */}
        {stage.id === "preview" && (
          <Section title="Preview" icon={stage.icon}>
            {data.summary && <p className="text-[13px] text-slate-300 leading-relaxed">{data.summary}</p>}
            {data.lane_position_notes && (
              <p className="text-[12px] text-slate-300 mt-2"><b>Lane position:</b> {data.lane_position_notes}</p>
            )}
            <Bullets label="Give-way points" items={data.give_way_points} tone="amber" />
            <Bullets label="Hazard cues" items={data.hazard_cues} tone="rose" />
            <NextBtn onClick={() => setStageIdx(1)} label="I've previewed it →" testid="ls-next-preview" />
          </Section>
        )}

        {stage.id === "understand" && (
          <Section title="Understand" icon={stage.icon}>
            <p className="text-[12px] text-slate-300 mb-2">Read it. Picture it. Decide if you're locked in.</p>
            <Bullets label="What to remember" items={data.learner_prompts} tone="indigo" />
            {data.safety_notes?.length > 0 && <Bullets label="Safety notes" items={data.safety_notes} tone="amber" />}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                data-testid="ls-need-help"
                onClick={() => setReadiness("need_help")}
                disabled={savingReadiness}
                className="px-3 py-2.5 text-xs font-extrabold rounded-xl bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-60 inline-flex items-center justify-center gap-1"
              >
                <HelpCircle className="w-4 h-4" /> I need help
              </button>
              <button
                data-testid="ls-understand"
                onClick={() => setReadiness("understand")}
                disabled={savingReadiness}
                className="px-3 py-2.5 text-xs font-extrabold rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60 inline-flex items-center justify-center gap-1"
              >
                <CheckCircle2 className="w-4 h-4" /> I understand
              </button>
            </div>
          </Section>
        )}

        {stage.id === "ask" && (
          <Section title="Ask better questions" icon={stage.icon}>
            <p className="text-[12px] text-slate-300 mb-2">Try asking your instructor:</p>
            <Bullets items={data.questions_to_ask} tone="indigo" />
            <NextBtn onClick={() => setStageIdx(3)} label="Got my questions →" testid="ls-next-ask" />
          </Section>
        )}

        {stage.id === "execute" && (
          <Section title="Execute" icon={stage.icon}>
            <p className="text-[12px] text-slate-300 mb-2">When you're driving the scenario, run this checklist in your head:</p>
            <Bullets label="Hazard cues" items={data.hazard_cues} tone="rose" />
            <Bullets label="Safety notes" items={data.safety_notes} tone="amber" />
            <NextBtn onClick={() => setStageIdx(4)} label="Done — let's reflect →" testid="ls-next-execute" />
          </Section>
        )}

        {stage.id === "reflect" && (
          <Section title="Reflect" icon={stage.icon}>
            <p className="text-[12px] text-slate-300 mb-2">Be honest with yourself. Three questions:</p>
            <Bullets items={data.reflection_questions} tone="indigo" />
            <textarea
              data-testid="ls-reflection-input"
              rows={4}
              value={reflection}
              onChange={(e) => setReflection(e.target.value.slice(0, 1500))}
              placeholder="What worked, what didn't, and what you'll change next time…"
              className="mt-2 w-full px-3 py-2 text-sm rounded-xl border border-slate-700 bg-slate-900 text-white resize-none"
            />
            <button
              data-testid="ls-reflection-save"
              onClick={saveReflection}
              disabled={savingReflection}
              className="mt-2 w-full px-3 py-2.5 text-xs font-extrabold rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60 inline-flex items-center justify-center gap-1"
            >
              {savingReflection ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save my reflection
            </button>
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-2xl bg-slate-900 border border-slate-700 p-4">
      <div className="flex items-center gap-2 mb-2 text-slate-400">
        <Icon className="w-4 h-4" />
        <p className="text-[11px] font-extrabold uppercase tracking-wider">{title}</p>
      </div>
      {children}
    </section>
  );
}

function Bullets({ label, items, tone = "slate" }) {
  if (!items || items.length === 0) return null;
  const tones = {
    indigo: "text-indigo-300",
    amber:  "text-amber-300",
    rose:   "text-rose-300",
    slate:  "text-slate-300",
  };
  return (
    <div className="mt-2">
      {label && <p className={`text-[10px] font-extrabold uppercase tracking-wider mb-1 ${tones[tone]}`}>{label}</p>}
      <ul className="space-y-1">
        {items.map((x, i) => (
          <li key={i} className="text-[12px] text-slate-300 leading-snug">• {x}</li>
        ))}
      </ul>
    </div>
  );
}

function NextBtn({ onClick, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="mt-3 w-full px-3 py-2.5 text-xs font-extrabold rounded-xl bg-slate-800 hover:bg-slate-700 text-white border border-slate-700"
    >{label}</button>
  );
}
